import React, { Suspense, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import ErrorBoundary from '../ErrorBoundary';
import { NotificationProvider } from '../../providers/NotificationContext';
import { getLaunchAt, isLaunchExemptPath, isLaunchGateHost, normalizeLaunchPath } from './launchPolicy';

const CountdownPage = React.lazy(() => import('./CountdownPage'));

/** A presentation gate only; backend authorization remains unchanged. */
export default function LaunchGate({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const path = normalizeLaunchPath(location.pathname);
  const launchRoute = path === '/launch' || path === '/countdown';
  const gatedHost = isLaunchGateHost(window.location.hostname);
  const launchAt = getLaunchAt();
  const deadline = Date.parse(launchAt);
  const [opened, setOpened] = useState(() => Date.now() >= deadline);

  useEffect(() => {
    if (opened || (!gatedHost && !launchRoute)) return undefined;
    let timeout: number | undefined;
    const refresh = () => {
      window.clearTimeout(timeout);
      const remaining = deadline - Date.now();
      if (remaining <= 0) setOpened(true);
      else timeout = window.setTimeout(refresh, Math.min(remaining, 2147483647));
    };
    refresh();
    // Background tabs can throttle timers. Recheck real time on resume and do
    // not re-close an already opened page if the device clock moves backward.
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [deadline, gatedHost, launchRoute, opened]);

  if (launchRoute && opened) return <Navigate to="/" replace />;
  if (path === '/countdown') {
    return <Navigate to={{ pathname: '/launch', search: location.search, hash: location.hash }} replace />;
  }
  if (launchRoute || (gatedHost && !opened && !isLaunchExemptPath(path))) {
    // Keep a requested unit/search URL intact. Its route is not mounted until
    // launch, then becomes available without a reload or another deployment.
    return <ErrorBoundary><NotificationProvider>
      <Suspense fallback={<Box sx={{ minHeight: '100svh', bgcolor: '#D75200', display: 'grid', placeItems: 'center' }}><CircularProgress sx={{ color: '#fff' }} /></Box>}>
        <CountdownPage launchAt={launchAt} />
      </Suspense>
    </NotificationProvider></ErrorBoundary>;
  }
  return <>{children}</>;
}
